

























































































































































































































































































































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import FormProvider from '@shared/components/forms/form-provider.vue';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import {
  AccessControlBadge,
  BadgeLinkedProduct,
} from '@access-control/interfaces/access-control-badge';
import {
  accessControlBadgeFields,
  accessControlProductDisplayConfig,
} from '@/config/access_control_options';
import Input from '@shared/components/forms/inputs/input/input.vue';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import Select from '@shared/components/forms/inputs/select/select.vue';
import Table from '@shared/components/tables/table.vue';
import DisplayFieldBadge from '@shared/components/displays/display-field-badge/display-field-badge.vue';
import DisplayFieldText from '@shared/components/displays/display-field-text/display-field-text.vue';
import Switch from '@shared/components/forms/inputs/switch/switch.vue';
import BlockAccessControlBadgeModal from '@/app/modules/access-control/components/modals/block-access-control-badge-modal/block-access-control-badge-modal.vue';
import ModulesHelper from '@core/services/modules.helper';

@Component({
  name: 'bpc-access-control-badge-form',
  components: {
    'bpc-form-provider': FormProvider,
    'bpc-input': Input,
    'bpc-entity-select': EntitySelect,
    'bpc-select': Select,
    'bpc-table': Table,
    'bpc-display-field-badge': DisplayFieldBadge,
    'bpc-display-field-text': DisplayFieldText,
    'bpc-switch': Switch,
    'bpc-block-access-control-badge-modal': BlockAccessControlBadgeModal,
  },
})
export default class AccessControlBadgeForm extends Vue {
  $refs!: {
    formProvider: InstanceType<typeof FormProvider> & {
      errorsObject: object;
      saveEntity(formModel: AnyEntity): Promise<BPCGenericApiResponse>;
      resetErrorObject(): void;
      saving: boolean;
    };
    contactSubscriptionsSelect: InstanceType<typeof EntitySelect> & {
      getEntities();
    };
  };

  // PROPS
  @Prop(Number) readonly modelId: number;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;
  @Prop({ type: Boolean, default: false }) readonly forPos: boolean;
  @Prop({
    type: Object,
    default() {
      return {};
    },
  })
  readonly value: AccessControlBadge;
  @Prop({ type: Object }) readonly initialData: Partial<AccessControlBadge>;
  @Prop({
    type: String,
  })
  readonly cancelButtonsPortalTarget: string;
  @Prop({
    type: String,
  })
  readonly tab: string;
  @Prop({
    type: String,
  })
  readonly saveButtonsPortalTarget: string;
  @Prop({ type: Boolean, default: false }) readonly showCancelButton: boolean;
  @Prop({ type: Boolean, default: true }) readonly showCodeField: boolean;
  @Prop({ type: Boolean, default: true }) readonly showTypeField: boolean;
  @Prop({ type: Boolean, default: true }) readonly showStatusField: boolean;
  @Prop({ type: Boolean, default: true }) readonly showBadgeNumberField: boolean;
  @Prop({ type: Boolean, default: true }) readonly showContactField: boolean;
  @Prop({ type: Boolean, default: true }) readonly showPrimaryField: boolean;
  @Prop({ type: Boolean, default: true }) readonly showSubscriptionField: boolean;
  @Prop({ type: Boolean, default: false }) readonly forceInitialDataOnSave: boolean;
  @Prop({ type: Boolean, default: true }) readonly showExternalId: boolean;
  @Prop({ type: Boolean, default: true }) readonly showBlockingReason: boolean;
  @Prop({ type: Boolean, default: false }) readonly showBlockButton: boolean;

  // DATA
  public showBlockAccessControlBadgeModal: boolean = false;
  public subscriptionsLoaded: boolean = false;
  public attendeesLoaded: boolean = false;
  public entriesLoaded: boolean = false;
  public enrollmentsLoaded: boolean = false;
  public bookingEventsLoaded: boolean = false;

  // COMPUTED
  get formModel(): AccessControlBadge {
    return this.value;
  }

  set formModel(value) {
    this.$emit('input', value);
  }

  get fieldsConfig() {
    return accessControlBadgeFields;
  }

  get displayConfig() {
    return accessControlProductDisplayConfig;
  }

  get selectedContactID() {
    return this.formModel?.contact_id;
  }

  get disablePrimary(): boolean {
    if (this.forPos) {
      return true;
    }
    return 'rfid' !== this.formModel?.type || 'active' !== this.formModel?.status;
  }

  get isEdition(): boolean {
    return !!this.formModel?.id;
  }

  get contactSubscriptionsFilters() {
    return [{ filterField: 'contact_id', value: this.selectedContactID }];
  }

  get subscriptionsToBadgeLinkedProducts(): BadgeLinkedProduct[] {
    return (this.formModel.subscriptions || [])
      .filter(subscription => {
        return !subscription.unlink;
      })
      .map(subscription => {
        return {
          id: subscription.id,
          type: 'subscription',
          code: subscription.code,
          status: subscription.status,
          description: `${subscription.category_name} | ${
            subscription.remaining_credits
          } | ${this.$bpcHelper.formatDate(subscription.start_date)} > ${this.$bpcHelper.formatDate(
            subscription.end_date,
          )}`,
        };
      });
  }

  get badgeLinkedProducts(): BadgeLinkedProduct[] {
    return this.subscriptionsToBadgeLinkedProducts.concat(
      [], // TODO remove this and add here other items to concat with
    );
  }

  get attendeesToBadgeLinkedProducts(): BadgeLinkedProduct[] {
    return (this.formModel.attendees || []).map(attendee => {
      return {
        id: attendee.id,
        type: 'attendee',
        code: attendee.code,
        status: attendee.status,
        description: attendee.session_name_date_time,
      };
    });
  }
  get entriesToBadgeLinkedProducts(): BadgeLinkedProduct[] {
    return (this.formModel.BCEntries || []).map(entry => {
      return {
        id: entry.id,
        type: 'entry',
        code: entry.code,
        status: entry.status,
        description: entry.entry_description,
      };
    });
  }

  get bookingEventsToBadgeLinkedProducts(): BadgeLinkedProduct[] {
    return (this.formModel.BCBookingEvents || []).map(bookingEvent => {
      return {
        id: bookingEvent.id,
        type: 'booking_event',
        code: bookingEvent.code,
        status: bookingEvent.status,
        description: bookingEvent.name,
      };
    });
  }

  get enrollmentsToBadgeLinkedProducts(): BadgeLinkedProduct[] {
    return (this.formModel.enrollments || []).map(enrollment => {
      return {
        id: enrollment.id,
        type: 'enrollment',
        code: enrollment.code,
        status: enrollment.status,
        description: enrollment.activity?.name,
      };
    });
  }

  get colBadgeNumber() {
    if (!this.forPos) {
      return this.showTypeField ? this.$bpcHelper.col8 : this.$bpcHelper.col12;
    }

    return this.$bpcHelper.col6;
  }

  get saving() {
    return this.$refs.formProvider.saving;
  }

  get entriesActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ENTRIES_MODULE_NAME);
  }

  get activitiesActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ACTIVITIES_MODULE_NAME);
  }

  get bookingEventsActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.BOOKINGS_MODULE_NAME);
  }

  // METHODS
  getSubscriptions() {
    this.subscriptionsLoaded = false;

    const url = this.$bpcApiRouteHelper.getEntityPath('subscription');
    this.$bpcApi
      .get(url, {
        params: {
          access_control_badge_id: this.modelId,
          orderBy: 'code',
          orderDirection: 'asc',
          columns: [
            'id',
            'center_id',
            'code',
            'status',
            'remaining_credits',
            'start_date',
            'end_date',
            'access_control_badge_id',
            'subscription_category_id',
          ],
          relationFields: ['subscriptionCategory'],
          computedFields: ['category_name'],
          per_page: -1,
        },
      })
      .subscribe(res => {
        this.formModel.subscriptions = res.data;
        this.subscriptionsLoaded = true;
      });
  }

  getEnrollments() {
    this.enrollmentsLoaded = false;
    const url = this.$bpcApiRouteHelper.getEntityPath('enrollment');
    this.$bpcApi
      .get(url, {
        params: {
          access_control_badge_id: this.modelId,
          orderBy: 'code',
          orderDirection: 'asc',
          columns: ['id', 'center_id', 'code', 'status', 'access_control_badge_id', 'activity_id'],
          per_page: -1,
          relationFields: ['activity'],
        },
      })
      .subscribe(res => {
        this.formModel.enrollments = res.data;
        this.enrollmentsLoaded = true;
      });
  }

  getAttendees() {
    this.attendeesLoaded = false;
    const url = this.$bpcApiRouteHelper.getEntityPath('activity-session-attendee');
    this.$bpcApi
      .get(url, {
        params: {
          access_control_badge_id: this.modelId,
          orderBy: 'code',
          orderDirection: 'asc',
          columns: [
            'id',
            'center_id',
            'code',
            'status',
            'access_control_badge_id',
            'activity_session_id',
          ],
          relationFields: ['activitySession'],
          computedFields: ['session_name_date_time'],
          per_page: -1,
        },
      })
      .subscribe(res => {
        this.formModel.attendees = res.data;
        this.attendeesLoaded = true;
      });
  }
  getEntries() {
    this.entriesLoaded = false;
    const url = this.$bpcApiRouteHelper.getEntityPath('entry');
    this.$bpcApi
      .get(url, {
        params: {
          access_control_badge_id: this.modelId,
          orderBy: 'code',
          orderDirection: 'asc',
          columns: [
            'id',
            'center_id',
            'code',
            'status',
            'access_control_badge_id',
            'entry_category_id',
          ],
          relationFields: ['entryCategory'],
          computedFields: ['entry_description'],
          per_page: -1,
        },
      })
      .subscribe(res => {
        this.formModel.BCEntries = res.data;
        this.entriesLoaded = true;
      });
  }

  getBookingEvents() {
    this.bookingEventsLoaded = false;
    const url = this.$bpcApiRouteHelper.getEntityPath('booking-event');
    this.$bpcApi
      .get(url, {
        params: {
          access_control_badge_id: this.modelId,
          orderBy: 'code',
          orderDirection: 'asc',
          columns: [
            'id',
            'center_id',
            'code',
            'status',
            'access_control_badge_id',
            'date',
            'start_time',
            'end_time',
            'name',
          ],
          per_page: -1,
        },
      })
      .subscribe(res => {
        this.formModel.BCBookingEvents = res.data;
        this.bookingEventsLoaded = true;
      });
  }

  saveEntity() {
    this.$refs.formProvider.saveEntity(this.formModel);
  }

  onEntitySaved(e) {
    this.$emit('entity-saved', e);
    this.onClose();
  }

  onClose() {
    this.$emit('close');
  }

  onEntityErrorSaving(e) {
    this.$bpcMessage.showErrorMessage({ text: 'core.superadmin_form_update_errors' });
    this.$emit('entity-error-saving', e);
  }

  onValidationWarning(e) {
    this.$emit('validation-warning', e);
  }

  onSavingEntity(event) {
    this.$emit('saving-entity', event);
  }

  async onModelLoaded(event) {
    console.log('MODEL LOADED', JSON.parse(JSON.stringify(event)));
    if (!this.formModel.id && this.initialData) {
      Object.keys(this.initialData).forEach(key => {
        this.$set(this.formModel, key, this.initialData[key]);
      });
    }
    this.getRelations();

    this.$emit('model-loaded', event);
  }

  prepareToSave(accessControlBadge: AccessControlBadge) {
    if (this.forceInitialDataOnSave && this.initialData) {
      Object.keys(this.initialData).forEach(key => {
        this.$set(accessControlBadge, key, this.initialData[key]);
      });
    }
    return accessControlBadge;
  }

  addSubscription() {
    console.log('OnAddSubscription');
  }

  // FIXME:
  removeSubscription(product: BadgeLinkedProduct) {
    // TODO check if it is better to do an api call to remove Subscription
    this.formModel.subscriptions = this.formModel.subscriptions.map(subscription => {
      if (subscription.id === product.id) {
        subscription.unlink = true;
      }
      return subscription;
    });
  }
  removeAttendee(product: BadgeLinkedProduct) {
    // TODO: Decide between implement something similar to unlink subscriptions or call the API
  }

  mounted() {
    // if (this.modelId) {
    //   this.getRelations();
    // }
  }

  getRelations() {
    this.getSubscriptions();
    this.getAttendees();
    if (this.entriesActivated) this.getEntries();
    if (this.activitiesActivated) this.getEnrollments();
    if (this.bookingEventsActivated) this.getBookingEvents();
  }

  clickOnBlock() {
    this.showBlockAccessControlBadgeModal = true;
  }

  onCloseBlockAccessControlBadgeModal() {
    this.showBlockAccessControlBadgeModal = false;
  }

  onBlockAccessControlBadgeModal() {
    this.showBlockAccessControlBadgeModal = false;
    this.$emit('on-badge-blocked');
    this.onClose();
  }

  // WATCHERS
  @Watch('selectedContactID')
  onChangeSelectedContactID() {
    this.$nextTick().then(() => {
      this.$refs.contactSubscriptionsSelect?.getEntities();
    });
  }

  @Watch('modelId')
  onChangeModelId() {
    // this.getRelations();
  }
}
